import _head from "lodash/head";
import React, { useEffect } from "react";
import Drawer from "web/react/components/drawer/drawer";
import FeedsFilters from "web/react/components/feeds-filters/feeds-filters";
import {
    PaginatedItem,
    usePageManager,
} from "web/react/components/feeds-filters/feeds-filters-pagination/pagination-state";
import { useFilterManager } from "web/react/components/feeds-filters/hooks/use-filter-manager";
import { useOverlayHandler } from "web/react/components/feeds-filters/hooks/use-overlay-handler";
import { useDesktopBreakpoint } from "web/react/hooks/use-breakpoint/use-breakpoint";
import withReduxProvider from "web/react/redux-provider";
import { gettext } from "web/script/modules/django-i18n";
import {
    CategoryFilterOption,
    isFilterSection,
    UniversalFiltersData,
} from "web/types/universal-filters";

interface FilterContainerProps {
    filters: UniversalFiltersData;
}
export function FilterContainer({ filters }: FilterContainerProps): JSX.Element {
    const {
        applyState,
        canApply,
        cancelState,
        displayedFilters,
        updateFromServer,
        ...otherFilterProps
    } = useFilterManager(filters);

    let pageManager = usePageManager(gettext("general.filters"));

    const { pushActivePage, paginationState, popPage, resetPagination } = pageManager;

    const { applyAndClose, cancelAndClose, overlayIsOpen } = useOverlayHandler(
        applyState,
        cancelState
    );

    const isDesktop = useDesktopBreakpoint();

    useEffect(() => {
        // Reset the pagination whenever the overlay closes
        if (!isDesktop && !overlayIsOpen) {
            resetPagination();
        }
        // The overlay state is the only important dep
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overlayIsOpen]);

    useEffect(() => {
        let categorySection = _head(
            displayedFilters.filter_section_list.filter(
                (section) => section.id === "section-category"
            )
        );
        if (!categorySection) {
            return;
        }

        if (new URLSearchParams(displayedFilters.query_string).has("product_type")) {
            return;
        }

        // open the category filter section on page load if no product type is selected
        pushActivePage([categorySection]);
        // We only ever want to run this hook once, the first time we have a filter_section_list
        // (some pages initialise with out a filter_section_list and then load it (e.g. /search/)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayedFilters.filter_section_list.length == 0]);

    useEffect(() => {
        if (!isDesktop && overlayIsOpen) {
            updateFromServer();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationState]);

    function onBack(): void {
        applyAndClose();
    }

    function handleItemClick(item: PaginatedItem): void {
        if (isFilterSection(item) && item.id == "section-category") {
            let options = item.option_groups[0].options as CategoryFilterOption[];
            let child = options.find((child) => child.checked);

            if (child) {
                pushActivePage([item, child]);
                return;
            }
        }
        pushActivePage([item]);
    }

    return (
        <Drawer
            title={gettext("general.filters")}
            id={"feeds-filter"}
            open={overlayIsOpen}
            onCancel={cancelAndClose}
            onBack={onBack}
            onOverlay={applyAndClose}
        >
            <FeedsFilters
                applyState={applyAndClose}
                canApply={canApply}
                displayedFilters={displayedFilters}
                isOpen={overlayIsOpen}
                onClose={popPage}
                onPageChange={handleItemClick}
                paginationState={paginationState}
                resetPagination={resetPagination}
                {...otherFilterProps}
            />
        </Drawer>
    );
}

export default withReduxProvider(FilterContainer);
